import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { DIALOG_DATA } from '../../../../global.variable';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { DialogRef } from '../modal-dialogue/modal-dialogue.ref';

@Component({
  selector: 'gtapp-media-preview',
  templateUrl: './media-preview.component.html',
  styleUrl: './media-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MediaPreviewComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() mediaSrc: any;
  @Input() mediaExtension: any;
  @Input() timeStamp: any;

  previewAndCloseOnImage: boolean = false;
  zoomPercentage: any;
  imageElement: any;

  @ViewChild('zoomLevel', { static: true })
  zoomLevelElement!: ElementRef<HTMLDivElement>;

  currentScale: number = 1;
  lastDistance: number = 0;
  maxScale: number = 3;
  minScale: number = 1;
  isPanning: boolean = false;
  startX: number = 0;
  startY: number = 0;
  translateX: number = 0;
  translateY: number = 0;
  constructor(
    @Optional() protected dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public dialogueData: any,
    private spinnerService: LoadingSpinnerService,
  ) {}
  ngOnInit(): void {
    if (this.dialogueData?.mediaSrc) {
      this.spinnerService.show('Loading');
      setTimeout(() => {
        this.hideSpinner();
      }, 2000);
      this.mediaSrc = this.dialogueData?.mediaSrc;
      this.mediaExtension =
        this.dialogueData?.mediaExtension || this.mediaExtension;
      this.previewAndCloseOnImage = true;
      this.timeStamp = this.dialogueData?.timeStamp;
    }
  }
  hideSpinner() {
    this.spinnerService.hide();
  }
  ngAfterViewInit(): void {
    let overlayContainer: any = document.querySelector(
      '.cdk-overlay-container',
    );

    if (overlayContainer) {
      overlayContainer.style['z-index'] = '1004';
      const overlayPanel = overlayContainer.querySelector('.overlay-panel');
      if (overlayPanel) {
        // Apply styles to make it fullscreen and position at the top
        Object.assign(overlayPanel['style'], {
          'width': '100%',
          'height': '100%',
          'top': '0',
          'left': '0',
          'transform': 'none',
          'position': 'fixed',
          'zIndex': '1000',
          'background-color': 'var(--bs-dark)',
        });
      }
    }
    setTimeout(() => {
      document.body.classList.add('no-scroll');
    }, 1);
    this.imageElement = document.getElementById('imageElement');

    if (!this.imageElement) return;
    const image = this.imageElement;

    // Add event listener for mouse wheel (desktop)
    image.addEventListener('wheel', (event: WheelEvent) => {
      event.preventDefault();
      this.zoomImage(
        event.deltaY < 0 ? 1.1 : 0.9,
        event.clientX,
        event.clientY,
      );
    });

    // Add event listener for touch (mobile)
    image.addEventListener('touchmove', (event: TouchEvent) => {
      if (event.touches.length === 2) {
        const newDistance = this.getDistance(event.touches);
        if (this.lastDistance) {
          const scale = newDistance / this.lastDistance;
          this.zoomImage(
            scale,
            event.touches[0].clientX,
            event.touches[0].clientY,
          );
        }
        this.lastDistance = newDistance;
      }
    });

    image.addEventListener('touchend', () => {
      this.lastDistance = 0; // Reset the distance when touch ends
    });
    image.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      const scaleFactor = this.currentScale >= 3 ? 1 / this.currentScale : 1.5;
      this.zoomImage(scaleFactor, event.clientX, event.clientY);
    });

    // Add event listeners for panning
    image.addEventListener('mousedown', (event: MouseEvent) =>
      this.startPan(event.clientX, event.clientY),
    );
    image.addEventListener('mousemove', (event: MouseEvent) =>
      this.pan(event.clientX, event.clientY),
    );
    image.addEventListener('mouseup', () => this.endPan());
    image.addEventListener('mouseleave', () => this.endPan());

    image.addEventListener('touchstart', (event: TouchEvent) => {
      if (event.touches.length === 1) {
        this.startPan(event.touches[0].clientX, event.touches[0].clientY);
      }
    });
    image.addEventListener('touchmove', (event: TouchEvent) => {
      if (event.touches.length === 1) {
        this.pan(event.touches[0].clientX, event.touches[0].clientY);
      }
    });
    image.addEventListener('touchend', () => this.endPan());
  }
  startPan(clientX: number, clientY: number): void {
    if (this.currentScale > 1 && this.currentScale <= 2) {
      // Only allow panning when zoomed in and not beyond 200%
      this.isPanning = true;
      this.startX = clientX - this.translateX;
      this.startY = clientY - this.translateY;
    }
  }

  pan(clientX: number, clientY: number): void {
    if (this.isPanning) {
      const image: any = this.imageElement;
      const container = image.parentElement.getBoundingClientRect();

      // Get the current image size after scaling
      const imageRect = image.getBoundingClientRect();
      const imageWidth = imageRect.width;
      const imageHeight = imageRect.height;

      // Calculate the difference between image size and container size
      const diffX = Math.max(0, (imageWidth - container.width) / 2);
      const diffY = Math.max(0, (imageHeight - container.height) / 2);

      // Calculate the new translation based on the cursor movement
      this.translateX = clientX - this.startX;
      this.translateY = clientY - this.startY;

      // Allow the image to partially cover the dialog title and close button
      const titleHeight = 50; // Adjust based on the title height
      const closeButtonWidth = 50; // Adjust based on the close button size

      // Constrain the translation so the image doesn't move outside the container
      const maxTranslateX = diffX - closeButtonWidth;
      const maxTranslateY = diffY - titleHeight;

      // Ensure translationX is within the bounds of the image in the container
      if (imageWidth > container.width) {
        this.translateX = Math.min(
          maxTranslateX,
          Math.max(-maxTranslateX, this.translateX),
        );
      } else {
        this.translateX = 0; // Center the image horizontally
      }

      // Ensure translationY is within the bounds of the image in the container
      if (imageHeight > container.height) {
        this.translateY = Math.min(
          maxTranslateY,
          Math.max(-maxTranslateY, this.translateY),
        );
      } else {
        this.translateY = 0; // Center the image vertically
      }

      // Update the transform with new translate values
      this.updateTransform();
    }
  }

  endPan(): void {
    this.isPanning = false;
  }

  updateTransform(): void {
    const image = this.imageElement;
    image.style.transform = `scale(${this.currentScale}) translate(${this.translateX}px, ${this.translateY}px)`;
  }

  zoomImage(scaleFactor: number, originX: number, originY: number): void {
    const newScale = this.currentScale * scaleFactor;

    if (newScale >= 1 && newScale <= 2) {
      this.currentScale = newScale;
    } else if (newScale > 2 && newScale <= 3) {
      this.currentScale = 2 + (newScale - 2) * 0.75; // Limit scaling between 200% and 225%
    } else if (newScale > 3) {
      this.currentScale = 3; // Cap the scale at 225%
    }
    if (this.currentScale === 1) {
      // Reset translate values to zero when zoom is 100%
      this.translateX = 0;
      this.translateY = 0;
    }

    const image = this.imageElement;
    const rect = image.getBoundingClientRect();
    const offsetX = (originX - rect.left) / rect.width;
    const offsetY = (originY - rect.top) / rect.height;

    image.style.transformOrigin = `${offsetX * 100}% ${offsetY * 100}%`;
    this.updateTransform();
    this.updateZoomLevelDisplay();
  }

  getDistance(touches: TouchList): number {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY,
    );
  }
  updateZoomLevelDisplay(): void {
    const zoomLevel = Math.round(this.currentScale * 100);
    if (zoomLevel) {
      this.zoomPercentage = `${zoomLevel}%`;
    }
    this.zoomLevelElement.nativeElement.innerText = `${zoomLevel}%`;
    this.zoomLevelElement.nativeElement.style.display = 'block';
    setTimeout(() => {
      this.zoomLevelElement.nativeElement.style.display = 'none';
    }, 3000);
  }

  onCloseDialogue() {
    this.dialogRef.close(false);
  }
  ngOnDestroy(): void {
    document.body.classList.remove('no-scroll');
    var ele: any = document.querySelector('.cdk-overlay-container');
    ele.style.backgroundColor = null;
    ele.style['z-index'] = '1000';
    const overlayPanel = ele.querySelector('.overlay-panel');
    if (overlayPanel) {
      Object.assign(overlayPanel['style'], {});
    }
  }
}
